/**
 * Tenant-authored labels can contain HTML (the Terms checkbox links to the policy page). We keep
 * a small allow-list and drop everything else, including all attributes except a safe `href`.
 */
const ALLOWED_TAGS = new Set(['a', 'strong', 'b', 'em', 'i', 'u', 'br', 'p', 'span', 'ul', 'ol', 'li']);
const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return escapeHtml(html);
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html');

  const walk = (node: Node): string => {
    if (node.nodeType === TEXT_NODE) return escapeHtml(node.textContent ?? '');
    if (node.nodeType !== ELEMENT_NODE) return '';
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style' || tag === 'iframe' || tag === 'object') return '';
    const inner = Array.from(el.childNodes).map(walk).join('');
    if (!ALLOWED_TAGS.has(tag)) return inner;
    if (tag === 'br') return '<br>';
    if (tag === 'a') {
      const href = (el.getAttribute('href') ?? '').trim();
      if (!SAFE_HREF.test(href)) return inner;
      const external = /^https?:/i.test(href) ? ' target="_blank" rel="noopener noreferrer"' : '';
      return `<a href="${escapeHtml(href)}"${external}>${inner}</a>`;
    }
    return `<${tag}>${inner}</${tag}>`;
  };

  return Array.from(doc.body.childNodes).map(walk).join('');
}

/** Plain text of an HTML label (for aria-labels and error messages). */
export function htmlToText(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]+>/g, '').trim();
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
